import {
    Button,
    FormControlLabel,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import LassoCheckbox from '../../../../components/form/LassoCheckbox';
import LassoRadioGroup from '../../../../components/form/LassoRadioGroup';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import QueryCard from '../QueryCard';
import {
    addInstrument,
    removeInstrument,
    selectAgeRange,
    selectConstructs,
    selectInstruments,
    selectRunning,
    selectSummary,
    selectWizardOperators, setAgeRange, setConstructs,
    setWizardOperators,
} from '../../../data-wrangler/dataWranglerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { colours } from '../../../../StyleConstants';
import { filterOperators } from '../../../data-wrangler/UtilityFunctions';
import LassoSelect from '../../../../components/LassoSelect';
import { LassoTextField } from '../../../../components/LassoTextField';
import Fuse from 'fuse.js';
import SaveIcon from '@mui/icons-material/Save';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useWizardQuery } from '../../../../api/dataWranglerApiSlice';
import { useUserInfoQuery } from '../../../../api/userApiSlice';
import { FilterFlow, HoverData, QueryType } from '../QueryWizard';
import { selectSelectedDatabase } from '../../../common/databaseSlice';
import { useGetInstrumentFieldCountMutation } from "../../../../api/dataDictionaryApiSlice";

type InstrumentView = "grid" | "column";
export type Instrument = {
  description: string;
  id: number;
  name: string;
  staticInstrument: boolean;
  visitLabels: string[];
  domains: string[];
}

type Props = {
  instruments: Instrument[];
  runQueryModalOpen: boolean;
  setRunQueryModalOpen: Dispatch<SetStateAction<boolean>>;
  setOpenSaveQueryModal: Dispatch<SetStateAction<boolean>>;
  hoverTarget: HoverData;
  setHoverTarget: Dispatch<SetStateAction<HoverData>>;
  filterFlow: FilterFlow;
  setFilterFlow: Dispatch<SetStateAction<FilterFlow>>;
  searchFilter: string;
  setSearchFilter: Dispatch<SetStateAction<string>>;
  setTooManySelected: (arg0: boolean) => void;
}

type InstrumentFilter = "alphabetical" | "selected" | "enabled" | "disabled";

const fuseOptions = {
    shouldSort: true,
    minMatchCharLength: 1,
    keys: [
        "name",
        "description"
    ]
};

const InstrumentSelector = ({ instruments, setRunQueryModalOpen, hoverTarget, setHoverTarget, setOpenSaveQueryModal, filterFlow, setFilterFlow, searchFilter, setSearchFilter, setTooManySelected }: Props) => {
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data } = useWizardQuery({ database: selectedDatabase });
    const { data: userData } = useUserInfoQuery({});

    const [view, setView] = useState<InstrumentView>("grid");
    const dispatch = useDispatch();

    const selectedSessions = useSelector(selectAgeRange);
    const selectedInstruments = useSelector(selectInstruments);
    const selectedDomains = useSelector(selectConstructs);

    const selectedSessionsSet = useMemo(() => new Set(selectedSessions), [selectedSessions]);
    const selectedInstrumentsSet = useMemo(() => new Set(selectedInstruments), [selectedInstruments]);
    const selectedDomainsSet = useMemo(() => new Set(selectedDomains), [selectedDomains]);

    const operators = useSelector(selectWizardOperators);
    const isSummaryRunning = useSelector(selectRunning);
    const summary = useSelector(selectSummary);

    const [instrumentFilter, setInstrumentFilter] = useState<InstrumentFilter>("alphabetical");
    const [hideDisabledInstruments, setHideDisabledInstruments] = useState(false);

    const [triggerFieldCount] = useGetInstrumentFieldCountMutation()

    const role = userData ? userData.roles : ["SUMMARY"];
    const participantCount = summary ? summary.total : 0;
    const selectionHasOnlyStaticInstruments =
    selectedInstruments.length > 0
    && selectedInstruments.every((instrument: string) => data.allInstruments[instrument]?.staticInstrument);



    const isRunQueryEnabled =
      ((role.includes("ADMIN") || role.includes("SUBJECT"))
      && (!window?.env?.onlyDuc || role.includes("ADMIN")) //If there is a staff admin role the user should be able to execute queries regardless of onlyDUC
      && (!(selectedSessions.length === 0) || selectionHasOnlyStaticInstruments)
      && !(selectedInstruments.length === 0)
      && participantCount > 0
      && !isSummaryRunning.active
      );

    const selectedCount = summary ? summary.total : 0;

    const queryTabularButtonTooltipTitle =
      window?.env?.onlyDuc && !role.includes("ADMIN") ?
          "This button is temporarily disabled, when full data download is available it will be open for users who have a valid DUC and have passed training" :
          !role.includes("SUBJECT") && !role.includes("ADMIN")?
              'Complete Data Use Certification (DUC) to access all features.' :
              selectedInstruments.length === 0 ?
                  'Please ensure you have selected an instrument before running a query.' :
                  selectedCount === 0 ?
                      'Please ensure your selected fields contain at least one candidate.' :
                      selectedSessions.length === 0 ?
                          'Please ensure you have selected a session before running a query.':
                          'Please ensure you have selected both a session and an instrument before running a query and your selected fields contain at least one candidate';


    const gridTemplateColumns = (view: InstrumentView) => {
        switch (view) {
        case "grid":
            return "repeat(3, minmax(100px, 1fr))";
        case "column":
            return "repeat(1, 1fr)";

        }
    }

    const onInstrumentClick = (instrument: Instrument, selectedInstruments: string[],) => {
        if (selectedInstruments.includes(instrument.name)) {
            dispatch(removeInstrument(instrument.name));
        } else {
            dispatch(addInstrument(instrument.name));
        }
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering: {
            return;
        }
        case FilterFlow.DomainBasedFiltering: {
            return;
        }
        case FilterFlow.InstrumentBasedFiltering: {
            const instrumentSessions = new Set([
                ...selectedSessions,
                ...(instrument?.visitLabels || [])
            ])
            dispatch(setAgeRange(Array.from(instrumentSessions)));
            return;
        }
        case FilterFlow.None: {
            setFilterFlow(FilterFlow.InstrumentBasedFiltering);
            const instrumentSessions = new Set([
                ...selectedSessions,
                ...(instrument?.visitLabels || [])
            ])
            dispatch(setAgeRange(Array.from(instrumentSessions)));
            return;
        }
        }
    }

    const isDisabled = (instrument: Instrument, domains: string[] | undefined, selectedSessions: string[]) => {
        if(instrument?.staticInstrument) return false;
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering:
            for(const session of (instrument?.visitLabels || [])) {
                if(selectedSessionsSet.has(session)) {
                    return false;
                }
            }
            return true;
        case FilterFlow.DomainBasedFiltering: {
            for(const session of (instrument?.visitLabels || [])) {
                if(selectedSessionsSet.has(session)) {
                    const atLeastOneDomainSelected = domains?.reduce((prev, curr) => prev || selectedDomainsSet.has(curr), false);
                    return selectedSessions.length === 0 || !atLeastOneDomainSelected;
                }
            }
            return true;
        }
        case FilterFlow.InstrumentBasedFiltering:
            return false;
        case FilterFlow.None:
            return false;
        }
    }


    const changeOperator = (field: string, value: any) => {
        dispatch(setWizardOperators({
            field: field,
            value: value
        }))
    }

    const onInstrumentFilterChange = (newInstrumentFilter: InstrumentFilter) => {
        setInstrumentFilter(newInstrumentFilter);
    }

    const filter = (instrument: Instrument) => {
        if(instrument?.staticInstrument) return true;
        if (!hideDisabledInstruments) {
            return true;
        }
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering: {
            for (const session of (instrument?.visitLabels || [])){
                if(selectedSessionsSet.has(session)){
                    return true;
                }
            }
        }
            return false;
        case FilterFlow.DomainBasedFiltering: {
            for (const domain of instrument.domains) {
                if (selectedDomainsSet.has(domain)) {
                    const instrumentInSelectedDomains = instrument.domains?.reduce((prev, curr) => selectedDomainsSet.has(curr) || prev, false);
                    return instrumentInSelectedDomains
                }
            }
            return false;
        }
        case FilterFlow.InstrumentBasedFiltering:{
            return true;
        }
        default: {
            return true;
        }
        }
    }

    const sessionOrderedInstruments = (instruments: Instrument[]) => {
        if(instrumentFilter === "alphabetical"){
            const fuse = new Fuse(instruments, fuseOptions);
            return !searchFilter
                ? instruments?.sort((a, b) => a.description.localeCompare(b.description))
                : fuse.search(searchFilter).map((result) => result.item);
        }
        let selected = [];
        let enabled = [];
        let disabled = [];

        const isInstrumentEnabled = (instrument: Instrument) => {
            if(instrument?.staticInstrument){
                return true;
            }
            switch(filterFlow) {
            case FilterFlow.SessionBasedFiltering:
                return instrument?.visitLabels?.reduce((prev, curr) => selectedSessionsSet.has(curr) || prev, false);
            case FilterFlow.DomainBasedFiltering: {
                const instrumentInSelectedDomains = instrument.domains?.reduce((prev, curr) => selectedDomainsSet.has(curr) || prev, false);
                const instrumentHasSelectedSession = instrument.visitLabels?.reduce((prev, curr) => selectedSessionsSet.has(curr) || prev, false);
                return instrumentInSelectedDomains && instrumentHasSelectedSession;
            }
            case FilterFlow.InstrumentBasedFiltering:
                return true;
            default:
                return true;
            }
        }

        // Partition instruments by its selection state
        for(let i = 0; i < instruments.length; i++) {
            const instrument = instruments[i];

            if(selectedInstrumentsSet.has(instrument.name)) {
                selected.push(instrument);
                // At least one instrument session is selected
            }else if(isInstrumentEnabled(instrument)) {
                enabled.push(instrument);
            }else {
                disabled.push(instrument);
            }
        }

        // Sort each partition by alphabetical order and by its fuzzy search ranking
        let fuse = new Fuse(selected, fuseOptions);
        selected = !searchFilter
            ? selected?.sort((a, b) => a.description.localeCompare(b.description))
            : fuse.search(searchFilter).map((result) => result.item);
        fuse = new Fuse(enabled, fuseOptions);
        enabled = !searchFilter
            ? enabled?.sort((a, b) => a.description.localeCompare(b.description))
            : fuse.search(searchFilter).map((result) => result.item);
        fuse = new Fuse(disabled, fuseOptions);
        disabled = !searchFilter
            ? disabled?.sort((a, b) => a.description.localeCompare(b.description))
            : fuse.search(searchFilter).map((result) => result.item);

        // Sort each partition by the instrument filter.
        switch(instrumentFilter) {
        case "selected":
            return [
                ...selected,
                ...enabled,
                ...disabled
            ]
        case "enabled":
            return [
                ...enabled,
                ...selected,
                ...disabled
            ]
        case "disabled":
            return [
                ...disabled,
                ...enabled,
                ...selected
            ]
        default:
            return [];
        }
    }

    const handleMouseEnter = (instrument: Instrument) => {
        setHoverTarget({
            type: QueryType.INSTRUMENT,
            data: {
                sessions: instrument.visitLabels || [],
                researchDomains: instrument.domains || [],
                instruments: [],
            }
        });
    }

    const handleMouseLeave = () => {
        setHoverTarget({
            type: QueryType.NONE,
            data: {
                sessions: [],
                researchDomains: [],
                instruments: [],
            }
        });
    }

    const isHighlighted = (instrument: Instrument) => {
        if(hoverTarget.type === QueryType.SESSION || hoverTarget.type === QueryType.RESEARCH_DOMAIN) {
            return hoverTarget.data.instruments.includes(instrument.name);
        }else if(selectedSessions.length === 0){
            return selectedInstrumentsSet.has(instrument.name);
        } else {
            return false;
        }
    }

    useEffect(() => {
        if(filterFlow === FilterFlow.InstrumentBasedFiltering && selectedInstruments.length === 0) {
            dispatch(setAgeRange([]));
            dispatch(setConstructs([]));
            setFilterFlow(FilterFlow.None);
        }
    }, [selectedInstruments]);

    const triggerDownload = async () => {
        const count = await triggerFieldCount(selectedInstruments)
        if (count.data > 16000) {
            setTooManySelected(true)
        } else {
            setRunQueryModalOpen(true)
        }
    }

    return (
        <Paper sx={{ minWidth: "40%", padding: 3, flexGrow: 1 }}>
            <Stack height={"100%"} gap={3}>
                <Stack flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <Typography variant={'h2'} fontSize={'18px'} fontWeight={'bold'}>Select instruments for tabulated query <span
                        style={{ fontSize: '12px', color: colours.error.error }}>(Required)</span></Typography>
                    <ToggleButtonGroup
                        id={'query-wizard-instrument-layout-button-group'}
                        size={"small"}
                        exclusive
                        value={view}
                        onChange={(e, newView) => {
                            if (newView) {
                                setView(newView)
                            }
                        }}
                    >
                        <ToggleButton id="query-wizard-instrument-grid-layout-button" value="grid" aria-label="grid">
                            <CalendarViewMonthIcon />
                        </ToggleButton>
                        <ToggleButton id="query-wizard-instrument-column-layout-button" value="column" aria-label="column">
                            <TableRowsIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                <LassoTextField
                    id={"query-wizard-search-text-filter"}
                    label={"Search (Use quotation marks to search for an exact match)"}
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                />

                <Stack flexDirection={"row"} justifyContent={"space-between"} marginX={2}>
                    <FormControlLabel
                        control={
                            <LassoCheckbox
                                id={"query-wizard-hide-filtered-instruments-checkbox"}
                                size={"small"}
                                sx={{
                                    padding: 1
                                }}
                                checked={hideDisabledInstruments}
                                onChange={(e, checked) => {
                                    setHideDisabledInstruments(checked);
                                }}
                            />
                        }
                        label="Hide filtered instruments"
                        sx={{
                            ".MuiFormControlLabel-label": {
                                fontSize: "0.9rem",
                            }
                        }}
                    />
                    <LassoSelect
                        id={"query-wizard-instrument-filter-dropdown"}
                        options={[
                            { value: "alphabetical", label: "Alphabetical" },
                            { value: "selected", label: "Selected" },
                            { value: "enabled", label: "Enabled" },
                            { value: "disabled", label: "Disabled" },
                        ]}
                        value={instrumentFilter}
                        onChange={(e) => onInstrumentFilterChange(e.target.value as InstrumentFilter)}
                    />
                </Stack >
                <Box
                    display={"grid"}
                    gap={2}
                    gridAutoRows={view === "grid" ? "minmax(min-content, max-content)" : "minmax(min-content, max-content)"}
                    gridTemplateColumns={gridTemplateColumns(view)}
                    flex={"1 1 auto"}
                    overflow={"auto"}
                    minHeight={"0px"}
                    padding={2}
                >
                    {
                        sessionOrderedInstruments(instruments)
                            .filter(filter)
                            .map((instrument) =>
                                <QueryCard
                                    id={`query-wizard-instrument-${instrument.description}`}
                                    key={instrument.id}
                                    label={instrument.description}
                                    topSubtext={instrument.domains?.join(", ")}
                                    centeredText={view === "grid"}
                                    onClick={() => onInstrumentClick(instrument, selectedInstruments)}
                                    selected={selectedInstrumentsSet.has(instrument.name)}
                                    disabled={isDisabled(instrument, instrument.domains, selectedSessions)}
                                    onMouseEnter={() => handleMouseEnter(instrument)}
                                    onMouseLeave={handleMouseLeave}
                                    highlighted={isHighlighted(instrument) || (selectedInstrumentsSet.has(instrument.name) && isDisabled(instrument, instrument.domains, selectedSessions))}
                                >
                                    {
                                        instrument?.staticInstrument
                                            ? <StaticInstrumentTag disabled={!isDisabled(instrument, instrument.domains, selectedSessions)} view={view}/>
                                            : <></>

                                    }
                                    <Stack
                                        gap={3}
                                        flexDirection={view === "grid" ? "column" : "row-reverse"}
                                        justifyContent={view === "grid" ? "initial" : "space-between"}
                                    >
                                        <Typography
                                            marginLeft={view === "grid" ? 0 : 3}
                                            fontSize={"10px"}
                                            fontWeight={"bold"}
                                            color={isDisabled(instrument, instrument.domains, selectedSessions) ? colours.gray.gray40 : colours.midnightBlue.blue160}
                                            textAlign={view === "grid" ? "center" : "right"}
                                            sx={{
                                                wordBreak: "break-word",
                                                flexBasis: "30%"
                                            }}
                                        >
                                            {instrument.domains?.join(", ")}
                                        </Typography>
                                        <Typography
                                            marginLeft={view === "grid" ? 0 : 3}
                                            fontSize={"12px"}
                                            fontWeight={"bold"}
                                            textAlign={view === "grid" ? "center" : "initial"}
                                            color={isDisabled(instrument, instrument.domains, selectedSessions) ? colours.gray.gray40 : colours.midnightBlue.blue160}
                                            sx={{
                                                flexBasis: "70%"
                                            }}
                                        >
                                            {instrument.description}
                                        </Typography>
                                    </Stack>
                                </QueryCard>
                            )}
                </Box>
                <Stack gap={2}>
                    <LassoRadioGroup
                        row
                        id={`query-wizard-and-or-participant-radio-group`}
                        onChange={(e) => changeOperator('instrument', e.target.value)}
                        value={operators.instrument}
                        options={filterOperators}
                        FormControlLabelProps={{
                            sx: {
                                ".MuiFormControlLabel-label": {
                                    fontSize: "0.9rem",
                                }
                            }
                        }
                        }
                    />
                    <Stack flexDirection={"row"} gap={3}>
                        <Button
                            id={"query-wizard-save-query-button"}
                            variant={"secondary"}
                            startIcon={<SaveIcon />}
                            disabled={!isRunQueryEnabled}
                            onClick={() => setOpenSaveQueryModal(true)}
                        >
              Save
                        </Button>
                        {/* Wrapping button in span is necessary for Tooltip to work when button is disabled*/}
                        <Tooltip title={!isRunQueryEnabled ? queryTabularButtonTooltipTitle : ""} arrow>
                            <span style={{ width: "100%" }}>
                                <Button
                                    id={"query-wizard-run-query-button"}
                                    variant={"primary"}
                                    fullWidth
                                    startIcon={<QueryStatsIcon />}
                                    disabled={!isRunQueryEnabled}
                                    onClick={triggerDownload}
                                >
                  Run Tabulated Query
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

            </Stack>
        </Paper>
    )
}


const StaticInstrumentTag =  ({ disabled, view }: {disabled: boolean, view: InstrumentView}) => {
    return (
        <Stack sx={{
            position: "absolute",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "10px",
            fontWeight: "bold",
            top: 0,
            left: 0,
            backgroundColor: disabled ? colours.orange.orange80 : colours.gray.gray20,
            borderTopLeftRadius: 3,
            borderBottomLeftRadius: view === "grid" ? 0 : 3,
            borderBottomRightRadius: view === "grid" ? 3 : 0,
            padding: 1,
            height: view === "grid" ? "initial" : "100%",
        }}>
        S
        </Stack>
    )
}

export default InstrumentSelector;
